import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, Users, Wallet, CheckCircle2, Clock, HourglassIcon, ImageOff, Loader2, Lock, Unlock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { ADMIN_PAGE } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatRupiah = (n) => `Rp${(n || 0).toLocaleString("id-ID")}`;

function getStatus(r) {
  if (!r.bukti_transfer_path) return { key: "menunggu_bayar", label: "Menunggu Bayar", className: "bg-sky-500/15 text-sky-400 border border-sky-500/30", Icon: HourglassIcon };
  if (!r.status_verifikasi) return { key: "menunggu_verifikasi", label: "Menunggu Verifikasi", className: "bg-amber-500/15 text-amber-400 border border-amber-500/30", Icon: Clock };
  return { key: "sudah_dicek", label: "Sudah Dicek", className: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30", Icon: CheckCircle2 };
}

export default function Admin() {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [registrasiDibuka, setRegistrasiDibuka] = useState(true);
  const [togglingSettings, setTogglingSettings] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifyingId, setVerifyingId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const fetchData = async () => {
    try {
      const [regRes, statsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/registrations`),
        axios.get(`${API}/registrations/stats`),
        axios.get(`${API}/settings`),
      ]);
      setRegistrations(regRes.data);
      setStats(statsRes.data);
      setRegistrasiDibuka(settingsRes.data.registrasi_dibuka);
    } catch (err) {
      toast.error("Gagal memuat data pendaftar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggleRegistrasi = async () => {
    setTogglingSettings(true);
    try {
      const res = await axios.patch(`${API}/settings/toggle`);
      setRegistrasiDibuka(res.data.registrasi_dibuka);
      toast.success(res.data.registrasi_dibuka ? "Pendaftaran baru dibuka" : "Pendaftaran baru ditutup");
    } catch (err) {
      toast.error("Gagal mengubah status pendaftaran");
    } finally {
      setTogglingSettings(false);
    }
  };

  const handleVerify = async (id) => {
    setVerifyingId(id);
    try {
      const res = await axios.patch(`${API}/registrations/${id}/verify`);
      setRegistrations((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      setStats((prev) =>
        prev
          ? { ...prev, sudah_verifikasi: prev.sudah_verifikasi + 1, belum_verifikasi: prev.belum_verifikasi - 1 }
          : prev
      );
      toast.success("Ditandai sudah dicek");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menandai pendaftaran");
    } finally {
      setVerifyingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return registrations;
    return registrations.filter(
      (r) =>
        r.nama.toLowerCase().includes(q) ||
        r.whatsapp.toLowerCase().includes(q) ||
        r.no_pendaftaran.toLowerCase().includes(q)
    );
  }, [registrations, search]);

  return (
    <div data-testid={ADMIN_PAGE.page} className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-1">
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Anton', sans-serif" }}>
            ADMIN &mdash; JAMBORE V
          </h1>
          <div className="flex items-center gap-3 bg-zinc-900/60 border border-white/10 rounded-full px-4 py-2">
            {registrasiDibuka ? <Unlock className="w-4 h-4 text-emerald-400" /> : <Lock className="w-4 h-4 text-red-400" />}
            <span className="text-sm text-white/70">Pendaftaran Baru: {registrasiDibuka ? "Dibuka" : "Ditutup"}</span>
            <Switch
              data-testid={ADMIN_PAGE.registrasiToggle}
              checked={registrasiDibuka}
              disabled={togglingSettings}
              onCheckedChange={handleToggleRegistrasi}
            />
          </div>
        </div>
        <p className="text-white/50 text-sm mb-8">Panitia BPK Oi Jakarta Barat &bull; Data pendaftar & verifikasi pembayaran</p>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <StatCard testid={ADMIN_PAGE.statTotalPendaftar} icon={Users} label="Total Pendaftar" value={stats.total_pendaftar} />
            <StatCard testid={ADMIN_PAGE.statTotalPeserta} icon={Users} label="Total Peserta" value={`${stats.total_peserta} orang`} />
            <StatCard testid={ADMIN_PAGE.statTotalDana} icon={Wallet} label="Total Dana" value={formatRupiah(stats.total_dana)} accent />
            <StatCard testid={ADMIN_PAGE.statSudahVerifikasi} icon={CheckCircle2} label="Sudah Dicek" value={`${stats.sudah_verifikasi}/${stats.total_pendaftar}`} />
          </div>
        )}

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            data-testid={ADMIN_PAGE.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama, WhatsApp, atau no. pendaftaran..."
            className="bg-zinc-900 border-white/15 text-white placeholder:text-white/30 pl-9"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/40">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Memuat data...
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid={ADMIN_PAGE.emptyState} className="text-center py-20 text-white/40">
            Belum ada pendaftar.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-white/10">
            <table data-testid={ADMIN_PAGE.table} className="w-full text-sm">
              <thead className="bg-zinc-900 text-white/50 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">No. Pendaftaran</th>
                  <th className="px-4 py-3 text-left">Nama</th>
                  <th className="px-4 py-3 text-left">WhatsApp</th>
                  <th className="px-4 py-3 text-left">Keluarga</th>
                  <th className="px-4 py-3 text-left">Peserta</th>
                  <th className="px-4 py-3 text-left">Total Bayar</th>
                  <th className="px-4 py-3 text-left">Bukti</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((r) => {
                  const status = getStatus(r);
                  return (
                    <tr data-testid={`${ADMIN_PAGE.rowPrefix}${r.id}`} key={r.id} className="hover:bg-white/5">
                      <td className="px-4 py-3 font-mono text-orange-400">{r.no_pendaftaran}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.nama}</div>
                        {r.bpkel && <div className="text-white/40 text-xs">BPKel: {r.bpkel}</div>}
                      </td>
                      <td className="px-4 py-3 text-white/70">{r.whatsapp}</td>
                      <td className="px-4 py-3">
                        <div className="text-white/70">{r.bawa_keluarga === "ya" ? `Ya (${r.jumlah_keluarga} orang)` : "Tidak"}</div>
                        {r.anggota_keluarga && (
                          <div className="text-white/40 text-xs whitespace-pre-line max-w-[180px]">{r.anggota_keluarga}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">{r.jumlah_peserta} orang</td>
                      <td className="px-4 py-3 font-semibold text-orange-400">{formatRupiah(r.total_bayar)}</td>
                      <td className="px-4 py-3">
                        {r.bukti_transfer_path ? (
                          <button
                            data-testid={`${ADMIN_PAGE.buktiThumbnailPrefix}${r.id}`}
                            onClick={() => setImagePreview(`${API}/files/${r.bukti_transfer_path}`)}
                            className="block w-14 h-14 rounded-lg overflow-hidden border border-white/10 hover:border-orange-500 transition-colors"
                          >
                            <img
                              src={`${API}/files/${r.bukti_transfer_path}`}
                              alt="Bukti transfer"
                              className="w-full h-full object-cover"
                              onError={(e) => (e.target.style.display = "none")}
                            />
                          </button>
                        ) : (
                          <span data-testid={`${ADMIN_PAGE.buktiBelumUploadPrefix}${r.id}`} className="text-white/30 text-xs italic">
                            Belum upload
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge data-testid={`${ADMIN_PAGE.statusBadgePrefix}${r.id}`} className={status.className}>
                          <status.Icon className="w-3 h-3 mr-1" /> {status.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {status.key === "menunggu_verifikasi" && (
                          <Button
                            data-testid={`${ADMIN_PAGE.verifyButtonPrefix}${r.id}`}
                            size="sm"
                            disabled={verifyingId === r.id}
                            onClick={() => handleVerify(r.id)}
                            className="bg-orange-600 hover:bg-orange-500 rounded-full text-xs"
                          >
                            {verifyingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "OK, Sudah Dicek"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!imagePreview} onOpenChange={(open) => !open && setImagePreview(null)}>
        <DialogContent data-testid={ADMIN_PAGE.imageDialog} className="bg-zinc-950 border-white/10 max-w-2xl">
          <DialogTitle className="sr-only">Bukti Transfer</DialogTitle>
          {imagePreview ? (
            <img src={imagePreview} alt="Bukti transfer" className="w-full rounded-lg" />
          ) : (
            <div className="flex items-center justify-center py-10 text-white/40">
              <ImageOff className="w-6 h-6 mr-2" /> Gambar tidak tersedia
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, accent }) {
  return (
    <div data-testid={testid} className="bg-zinc-900/60 border border-white/10 rounded-2xl p-4">
      <div className="flex items-center gap-2 text-white/40 text-xs mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-xl font-bold ${accent ? "text-orange-400" : "text-white"}`}>{value}</div>
    </div>
  );
}
